import { CloudUploadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { Alert, Button, Flex, List, Modal, Typography, UploadFile, UploadProps, message } from "antd";
import axios from "axios";
import Dragger from "antd/es/upload/Dragger";
import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { uploadReportVocSeries, UploadReportVocSeriesRow } from "src/services/fugitive.service";
import { componentPalette } from "src/theme/component-palette";
import { BASE_URL } from "src/utils/http-common";
import "./ReportVocSeriesUploadModal.css";

/** One data row = one series line; same year/area/equipment/service can span multiple rows (merged on server). */
const TEMPLATE_HEADERS = [
  "year",
  "area",
  "seriesName",
  "equipmentType",
  "service",
  "totalEquipmentPoint",
  "measuredEquipment",
  "exemptEquipment",
  "exceedingEquipment",
  "repairedEquipment",
  "emissionMeasured",
  "tvocs",
  "bz",
  "bd",
  "vcm",
  "edc",
] as const;

type CarryShape = {
  year: number;
  area: string;
  equipmentType: string;
  service: string;
  totalEquipmentPoint: number;
  measuredEquipment: number;
  exemptEquipment: number;
  exceedingEquipment: number;
  repairedEquipment: number;
  emissionMeasured: number;
  tvocs: number;
};

const TEMPLATE_FILENAME = "Fugitive-VOCsSeries-Upload-Template.xlsx";

const MAX_UPLOAD_MB = 50;

const normalizeCell = (v: unknown) => (v === null || v === undefined ? "" : String(v).trim());

type ParseFail = { ok: false; errors: string[] };
type ParseOk = { ok: true; rows: UploadReportVocSeriesRow[] };

const checkNumber = (v: unknown, field: string, rowNum: number): string | null => {
  if (v === "" || v === null || v === undefined) {
    return `Row ${rowNum}: "${field}" is required`;
  }
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) {
    return `Row ${rowNum}: "${field}" must be a number`;
  }
  return null;
};

const parseWorksheet = (worksheet: XLSX.WorkSheet): ParseOk | ParseFail => {
  const errors: string[] = [];
  const rowsAoA = XLSX.utils.sheet_to_json<(string | number | null | undefined)[]>(worksheet, {
    header: 1,
    defval: "",
  });
  if (!rowsAoA.length) {
    return { ok: false, errors: ["File is empty."] };
  }

  const rawHeader = (rowsAoA[0] || []).map((h) => normalizeCell(h).replace(/^\uFEFF/, ""));
  const header = rawHeader.map((h) => h.trim());

  const colIndex: Record<string, number> = {};
  header.forEach((h, i) => {
    if ((TEMPLATE_HEADERS as readonly string[]).includes(h) && colIndex[h] === undefined) {
      colIndex[h] = i;
    }
  });

  const missing = TEMPLATE_HEADERS.filter((h) => colIndex[h] === undefined);
  if (missing.length > 0) {
    errors.push(`Missing columns: ${missing.join(", ")}. Download the template and use the exact header row.`);
    return { ok: false, errors };
  }

  const out: UploadReportVocSeriesRow[] = [];
  let carry: CarryShape | null = null;

  for (let r = 1; r < rowsAoA.length; r++) {
    const row = rowsAoA[r] || [];
    const get = (key: string) => row[colIndex[key]];

    const seriesName = normalizeCell(get("seriesName"));
    const area = normalizeCell(get("area"));
    const equipmentType = normalizeCell(get("equipmentType"));
    const service = normalizeCell(get("service"));
    const yearCell = normalizeCell(get("year"));

    if (!seriesName && !area && !equipmentType && !service && !yearCell) {
      continue;
    }

    const rowNum = r + 1;
    const rowErrors: string[] = [];

    const mergedArea = area || carry?.area || "";
    const mergedEquipment = equipmentType || carry?.equipmentType || "";
    const mergedService = service || carry?.service || "";
    const mergedYearStr = yearCell || (carry != null ? String(carry.year) : "");

    if (!seriesName) {
      rowErrors.push(`Row ${rowNum}: "seriesName" is required (one series per row).`);
    }
    if (!mergedYearStr) {
      rowErrors.push(`Row ${rowNum}: "year" is required (or copy the block from the row above).`);
    }
    if (!mergedArea) {
      rowErrors.push(`Row ${rowNum}: "area" is required (or copy the block from the row above).`);
    }
    if (!mergedEquipment) {
      rowErrors.push(`Row ${rowNum}: "equipmentType" is required (or copy the block from the row above).`);
    }
    if (!mergedService) {
      rowErrors.push(`Row ${rowNum}: "service" is required (or copy the block from the row above).`);
    }

    const allowMetaCarry = carry != null;
    const metaNumFields = [
      "totalEquipmentPoint",
      "measuredEquipment",
      "exemptEquipment",
      "exceedingEquipment",
      "repairedEquipment",
      "emissionMeasured",
      "tvocs",
    ] as const;

    for (const field of metaNumFields) {
      const raw = get(field);
      if (raw === "" || raw === null || raw === undefined) {
        if (allowMetaCarry && carry) {
          const v = carry[field];
          if (typeof v === "number") continue;
        }
        rowErrors.push(`Row ${rowNum}: "${field}" is required (or copy from the row above).`);
        continue;
      }
      const msg = checkNumber(raw, field, rowNum);
      if (msg) rowErrors.push(msg);
    }

    for (const field of ["bz", "bd", "vcm", "edc"] as const) {
      const msg = checkNumber(get(field), field, rowNum);
      if (msg) rowErrors.push(msg);
    }

    if (mergedYearStr) {
      const yTest = Number(String(mergedYearStr).replace(/,/g, ""));
      if (!Number.isFinite(yTest)) {
        rowErrors.push(`Row ${rowNum}: "year" must be a number`);
      }
    }

    const etNorm = mergedEquipment.replace(/\s/g, "").toLowerCase();
    if (mergedEquipment && etNorm === "total") {
      rowErrors.push(`Row ${rowNum}: "Total" summary rows cannot be uploaded`);
    }

    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
      continue;
    }

    const y = Number(String(mergedYearStr).replace(/,/g, ""));
    const num = (key: (typeof TEMPLATE_HEADERS)[number]) => {
      const raw = get(key);
      if (typeof raw === "number" && Number.isFinite(raw)) {
        return raw;
      }
      return Number(String(raw ?? "").replace(/,/g, ""));
    };

    const pickMetaNum = (field: (typeof metaNumFields)[number]): number => {
      const raw = get(field);
      if (raw === "" || raw === null || raw === undefined) {
        return carry![field];
      }
      return num(field);
    };

    const rowPayload: UploadReportVocSeriesRow = {
      year: Math.round(y),
      area: mergedArea,
      seriesName,
      equipmentType: mergedEquipment,
      service: mergedService,
      totalEquipmentPoint: pickMetaNum("totalEquipmentPoint"),
      measuredEquipment: pickMetaNum("measuredEquipment"),
      exemptEquipment: pickMetaNum("exemptEquipment"),
      exceedingEquipment: pickMetaNum("exceedingEquipment"),
      repairedEquipment: pickMetaNum("repairedEquipment"),
      emissionMeasured: pickMetaNum("emissionMeasured"),
      tvocs: pickMetaNum("tvocs"),
      bz: num("bz"),
      bd: num("bd"),
      vcm: num("vcm"),
      edc: num("edc"),
    };

    out.push(rowPayload);

    carry = {
      year: rowPayload.year,
      area: rowPayload.area,
      equipmentType: rowPayload.equipmentType,
      service: rowPayload.service,
      totalEquipmentPoint: rowPayload.totalEquipmentPoint,
      measuredEquipment: rowPayload.measuredEquipment,
      exemptEquipment: rowPayload.exemptEquipment,
      exceedingEquipment: rowPayload.exceedingEquipment,
      repairedEquipment: rowPayload.repairedEquipment,
      emissionMeasured: rowPayload.emissionMeasured,
      tvocs: rowPayload.tvocs,
    };
  }

  if (out.length === 0) {
    if (errors.length === 0) {
      errors.push("No data rows found under the header.");
    }
    return { ok: false, errors };
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, rows: out };
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
};

const ReportVocSeriesUploadModal = ({ open, onClose, onSuccess }: Props) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsedRows, setParsedRows] = useState<UploadReportVocSeriesRow[] | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const fileError = useMemo(() => fileList.some((f) => f.status === "error"), [fileList]);

  const reset = () => {
    setFileList([]);
    setParsedRows(null);
    setValidationErrors([]);
    onClose();
  };

  const beforeUpload: UploadProps["beforeUpload"] = () => false;

  const handleChange: UploadProps["onChange"] = async (info) => {
    const { file, fileList } = info;

    if (file.status === "removed" || fileList.length === 0) {
      setFileList([]);
      setParsedRows(null);
      setValidationErrors([]);
      return;
    }

    const latest = fileList[fileList.length - 1] as UploadFile;
    setFileList([latest]);

    const rcFile = (latest.originFileObj as File | undefined) ?? (file.originFileObj as File | undefined) ?? null;

    setValidationErrors([]);
    setParsedRows(null);

    if (!rcFile) {
      if (file.status === "uploading") {
        return;
      }
      const err = "Could not read the file. Please try again or pick the file using the file chooser.";
      setValidationErrors([err]);
      message.error(err);
      setFileList([{ ...latest, status: "error" }]);
      return;
    }

    const displayFile = { ...latest, originFileObj: rcFile as UploadFile["originFileObj"] };
    const name = (displayFile.name || file.name || rcFile.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      const err = "Only .xlsx or .xls files are allowed.";
      setValidationErrors([err]);
      message.error(err);
      setFileList([{ ...displayFile, status: "error" }]);
      return;
    }

    const sizeMb = (displayFile.size ?? file.size ?? rcFile.size) / 1024 / 1024;
    if (sizeMb > MAX_UPLOAD_MB) {
      const err = `File must be smaller than ${MAX_UPLOAD_MB} MB.`;
      setValidationErrors([err]);
      message.error(err);
      setFileList([{ ...displayFile, status: "error" }]);
      return;
    }

    try {
      const data = await rcFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        const errs = ["No sheet found in the workbook."];
        setValidationErrors(errs);
        message.error(errs[0]);
        setFileList([{ ...displayFile, status: "error" }]);
        return;
      }
      const worksheet = workbook.Sheets[sheetName];
      const result = parseWorksheet(worksheet);

      if (!result.ok) {
        setValidationErrors(result.errors);
        message.error(`Validation failed (${result.errors.length} issue(s)). See details below.`);
        setFileList([{ ...displayFile, status: "error", response: result.errors }]);
        return;
      }

      setParsedRows(result.rows);
      message.success(`Validated ${result.rows.length} row(s).`);
      setFileList([{ ...displayFile, status: "done", size: rcFile.size, name: displayFile.name || rcFile.name }]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Invalid file.";
      setValidationErrors([msg]);
      message.error(msg);
      setFileList([{ ...displayFile, status: "error" }]);
    }
  };

  const onRemove: UploadProps["onRemove"] = () => {
    setParsedRows(null);
    setValidationErrors([]);
    setFileList([]);
    return true;
  };

  const onDownloadTemplate = () => {
    try {
      const sampleRow1: (string | number)[] = [
        new Date().getFullYear(),
        "Area name or area _id",
        "Line-A",
        "Valve",
        "All",
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ];
      const sampleRow2: (string | number)[] = ["", "", "Line-B", "", "", "", "", "", "", "", "", "", 0, 0, 0, 0];
      const aoa = [[...TEMPLATE_HEADERS], sampleRow1, sampleRow2];
      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

      const instructionsAoa: (string | number)[][] = [
        ["VOC Series upload — how to avoid repeating the same cells"],
        [""],
        [
          "Rule: ONE row = ONE seriesName + bz, bd, vcm, edc. Same year / area / equipment / service can span multiple rows; the server merges them.",
        ],
        [""],
        ["Row A — full block (fill every column in this row)"],
        [
          "• If the NEXT row is another species for the SAME equipment group, leave BLANK (do not copy-paste): year, area, equipmentType, service, totalEquipmentPoint through tvocs.",
        ],
        ["• On that next row, only fill: seriesName, bz, bd, vcm, edc. Empty cells are copied from the row above automatically."],
        [""],
        ["Important"],
        [
          "• Header row must match exactly (e.g. totalEquipmentPoint). Excel may shorten names in the column header bar — widen columns or use the template as-is.",
        ],
        [""],
        ["คำแนะนำ (ไทย)"],
        [
          "• แถวแรกของกลุ่ม: กรอก year, area, seriesName, equipmentType, service, ตัวเลขนับจุด/อุปกรณ์, emissionMeasured, tvocs และ bz–edc ครบ",
        ],
        [
          "• แถวถัดไปถ้ายังเป็นชุดเดียวกัน: อย่าคัดลอกค่าซ้ำ — เว้นว่าง year ถึง tvocs แล้วกรอกเฉพาะ seriesName กับ bz–edc (ระบบดึงค่าจากแถวบนให้)",
        ],
        ["• หัวคอลัมน์ต้องเหมือนใน Template เป๊ะ ห้ามตัดชื่อสั้นลง"],
      ];
      const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsAoa);
      XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

      XLSX.writeFile(workbook, TEMPLATE_FILENAME);
      message.success("Template downloaded.");
    } catch {
      message.error("Failed to download template.");
    }
  };

  const onSubmit = async () => {
    if (!parsedRows?.length) {
      message.warning("Please select a valid file first.");
      return;
    }
    try {
      setLoading(true);
      const response = await uploadReportVocSeries(parsedRows);
      if (response.data?.code === 200) {
        message.success("Upload completed successfully.");
        await onSuccess();
        reset();
      } else {
        Modal.error({
          title: "Upload failed",
          content: String(response.data?.message || "Upload failed."),
          okText: "OK",
          centered: true,
        });
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && !err.response) {
        const origin = BASE_URL.replace(/\/$/, "");
        Modal.error({
          title: "Cannot reach the API",
          content: `Cannot reach the API (${origin}). Start VOC-Backend on this port or update REACT_APP_BASE_URL in .env.`,
          okText: "OK",
          centered: true,
        });
        return;
      }
      const ax = err as { response?: { data?: { message?: string | string[] } } };
      const raw = ax.response?.data?.message;
      const apiMsg = Array.isArray(raw) ? raw.join(", ") : raw;
      Modal.error({
        title: "Upload failed",
        content: String(apiMsg || "Upload failed."),
        okText: "OK",
        centered: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadDisabled = !parsedRows?.length || fileError || loading;

  return (
    <Modal
      centered
      title={<ModalTitle title="Upload VOC Series" />}
      open={open}
      onCancel={reset}
      width={640}
      destroyOnClose
      okButtonProps={{ style: { display: "none" } }}
      cancelButtonProps={{ style: { display: "none" } }}
      styles={{
        body: { padding: "24px 28px 16px" },
        footer: { padding: "16px 28px 20px", borderTop: "1px solid #f0f0f0" },
      }}
      footer={
        <Flex justify="center" align="center">
          <Button type="primary" disabled={uploadDisabled} loading={loading} onClick={onSubmit} style={{ minWidth: 120 }}>
            Upload
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={16} style={{ width: "100%", alignItems: "stretch" }}>
        <div style={{ width: "100%" }}>
          <Dragger
            disabled={loading}
            multiple={false}
            accept=".xls,.xlsx"
            maxCount={1}
            fileList={fileList}
            showUploadList={{ showRemoveIcon: true }}
            listType="text"
            customRequest={() => {}}
            beforeUpload={beforeUpload}
            onChange={handleChange}
            onRemove={onRemove}
            style={{
              width: "100%",
              background: "#fafafa",
              borderRadius: 12,
              border: "1px dashed #d9d9d9",
              padding: "48px 32px 32px",
              minHeight: 200,
              margin: 0,
            }}
            rootClassName="report-voc-series-upload-dragger"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                width: "100%",
              }}
            >
              <CloudUploadOutlined style={{ fontSize: 48, color: "rgba(0, 0, 0, 0.72)" }} />
              <Typography.Text style={{ fontSize: 16, color: "rgba(0, 0, 0, 0.75)", fontWeight: 500 }}>
                Drag and Drop file here or Choose file
              </Typography.Text>
            </div>
          </Dragger>
          <Flex wrap="wrap" gap={12} align="center" justify="space-between" className="report-voc-series-upload-meta-row">
            <Flex gap={8} align="center" style={{ minWidth: 0 }}>
              <FileExcelOutlined style={{ fontSize: 22, color: "rgba(0, 0, 0, 0.55)" }} />
              <Typography.Link
                onClick={onDownloadTemplate}
                style={{
                  color: componentPalette.primary,
                  fontWeight: 500,
                  fontSize: 14,
                  wordBreak: "break-word",
                }}
              >
                {TEMPLATE_FILENAME}
              </Typography.Link>
            </Flex>
            <Typography.Text type="secondary" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
              • Only: xls, xlsx
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
              • Maximum Size: {MAX_UPLOAD_MB} MB
            </Typography.Text>
          </Flex>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            One sheet per file.
          </Typography.Text>
        </div>

        {parsedRows && parsedRows.length > 0 && !fileError && (
          <Alert type="success" showIcon message={`Ready to upload ${parsedRows.length} row(s).`} />
        )}

        {validationErrors.length > 0 && (
          <Alert
            type="error"
            showIcon
            message="Validation issues"
            description={
              <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}>
                <List
                  size="small"
                  dataSource={validationErrors}
                  renderItem={(item) => (
                    <List.Item style={{ padding: "4px 0", border: "none" }}>
                      <Typography.Text code style={{ fontSize: 12 }}>
                        {item}
                      </Typography.Text>
                    </List.Item>
                  )}
                />
              </div>
            }
          />
        )}
      </Flex>
    </Modal>
  );
};

export default ReportVocSeriesUploadModal;
